import { useEffect, useState } from "react";
import axios from "axios";
import { Save, Trash2, Loader2, Calendar, Users, Video, Clock, Eye, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cfgOpts = { withCredentials: true };

export const AdminMasterclass = () => {
  const [config, setConfig] = useState(null);
  const [regs, setRegs] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [activePreview, setActivePreview] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingCfg, setSavingCfg] = useState(false);

  const loadAll = async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/admin/masterclass/config`, cfgOpts),
        axios.get(`${API}/admin/masterclass/registrations`, cfgOpts),
      ]);
      setConfig(c.data);
      setRegs(r.data.registrations || []);
      try {
        const w = await axios.get(`${API}/admin/masterclass/waitlist`, cfgOpts);
        setWaitlist(w.data.waitlist || []);
      } catch (_) {
        setWaitlist([]);
      }
      try {
        const p = await axios.get(`${API}/admin/masterclass/email-preview`, cfgOpts);
        setPreviews(p.data.previews || []);
      } catch (_) {
        setPreviews([]);
      }
    } catch (e) {
      toast.error("Impossibile caricare i dati della masterclass.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const saveConfig = async () => {
    setSavingCfg(true);
    try {
      const res = await axios.put(
        `${API}/admin/masterclass/config`,
        {
          title: config.title,
          subtitle: config.subtitle,
          badge_label: config.badge_label || "",
          cta_label: config.cta_label || "",
          bullets: (config.bullets || []).map((b) => (b || "").trim()).filter(Boolean),
          surprise: config.surprise || "",
          manifesto: config.manifesto || "",
          event_datetime: config.event_datetime,
          price: config.price,
          spots_total: Number(config.spots_total) || 15,
          spots_manual_offset: Number(config.spots_manual_offset) || 0,
          banner_visible: Boolean(config.banner_visible),
        },
        cfgOpts
      );
      setConfig((c) => ({ ...c, ...res.data }));
      toast.success("Impostazioni salvate.");
      try {
        const p = await axios.get(`${API}/admin/masterclass/email-preview`, cfgOpts);
        setPreviews(p.data.previews || []);
      } catch (_) {}
    } catch (e) {
      toast.error("Errore nel salvataggio.");
    } finally {
      setSavingCfg(false);
    }
  };

  const deleteReg = async (id, name) => {
    if (!window.confirm(`Eliminare l'iscrizione di ${name}?`)) return;
    try {
      await axios.delete(`${API}/admin/masterclass/registrations/${id}`, cfgOpts);
      setRegs((prev) => prev.filter((r) => r.id !== id));
      toast.success("Iscrizione eliminata.");
    } catch (e) {
      toast.error("Errore nell'eliminazione dell'iscrizione.");
    }
  };

  if (loading) {
    return <p className="text-stone flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Caricamento…</p>;
  }
  if (!config) return null;

  const field = (k) => (e) => setConfig((c) => ({ ...c, [k]: e.target.value }));

  return (
    <div className="space-y-10" data-testid="admin-masterclass">
      {/* Config */}
      <section className="border border-espresso/10 bg-white/40 p-6 sm:p-8">
        <h3 className="font-display text-xl text-espresso mb-6 flex items-center gap-2">
          <Video size={18} className="text-fuchsiabrand" /> Impostazioni Guida Digitale
        </h3>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="admin-label">Titolo</label>
            <input className="admin-input" value={config.title || ""} onChange={field("title")} data-testid="mc-cfg-title" />
          </div>
          <div className="sm:col-span-2">
            <label className="admin-label">Sottotitolo</label>
            <textarea className="admin-input resize-none" rows={2} value={config.subtitle || ""} onChange={field("subtitle")} data-testid="mc-cfg-subtitle" />
          </div>
          <div>
            <label className="admin-label">Etichetta in alto (badge)</label>
            <input className="admin-input" placeholder="Es. Guida Digitale" value={config.badge_label || ""} onChange={field("badge_label")} data-testid="mc-cfg-badge" />
          </div>
          <div>
            <label className="admin-label">Testo del pulsante</label>
            <input className="admin-input" placeholder="Es. Richiedi la guida" value={config.cta_label || ""} onChange={field("cta_label")} data-testid="mc-cfg-cta" />
          </div>
          <div className="sm:col-span-2">
            <label className="admin-label">Contenuti (un punto per riga)</label>
            <textarea
              className="admin-input min-h-[140px] resize-y"
              value={(config.bullets || []).join("\n")}
              onChange={(e) => setConfig((c) => ({ ...c, bullets: e.target.value.split("\n") }))}
              data-testid="mc-cfg-bullets"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="admin-label">Sorpresa finale (riga staccata, in fucsia)</label>
            <input className="admin-input" placeholder="Es. … + sorpresa finale!" value={config.surprise || ""} onChange={field("surprise")} data-testid="mc-cfg-surprise" />
          </div>
          <div className="sm:col-span-2">
            <label className="admin-label">Frase manifesto (mostrata in evidenza)</label>
            <textarea
              className="admin-input min-h-[90px] resize-y"
              value={config.manifesto || ""}
              onChange={field("manifesto")}
              data-testid="mc-cfg-manifesto"
            />
          </div>
          <div>
            <label className="admin-label">Data e ora (testo mostrato)</label>
            <input className="admin-input" placeholder="Es. Giovedì 4 settembre, ore 21:00" value={config.event_datetime || ""} onChange={field("event_datetime")} data-testid="mc-cfg-datetime" />
          </div>
          <div>
            <label className="admin-label">Prezzo (testo mostrato)</label>
            <input className="admin-input" placeholder="Es. 27€" value={config.price || ""} onChange={field("price")} data-testid="mc-cfg-price" />
          </div>
          <div>
            <label className="admin-label">Posti totali</label>
            <input className="admin-input" type="number" min={1} value={config.spots_total ?? 15} onChange={field("spots_total")} data-testid="mc-cfg-spots" />
          </div>
          <div>
            <label className="admin-label">Posti occupati extra (non in elenco)</label>
            <input className="admin-input" type="number" min={0} value={config.spots_manual_offset ?? 0} onChange={field("spots_manual_offset")} data-testid="mc-cfg-offset" />
            <p className="text-stone/70 text-xs mt-1.5">
              Persone iscritte che non compaiono nell'elenco qui sotto (es. iscritte altrove). Il contatore del banner terrà conto anche di loro.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setConfig((c) => ({ ...c, banner_visible: !c.banner_visible }))}
          data-testid="mc-cfg-banner-visible"
          className="mt-6 flex items-center gap-3 group"
          role="switch"
          aria-checked={Boolean(config.banner_visible)}
        >
          <span
            className={`w-11 h-6 rounded-full relative transition-colors duration-300 ${
              config.banner_visible ? "bg-fuchsiabrand" : "bg-espresso/20"
            }`}
          >
            <span
              className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all duration-300 ${
                config.banner_visible ? "left-6" : "left-1"
              }`}
            />
          </span>
          <span className="text-sm text-espresso">
            Mostra il banner in cima al sito{" "}
            <span className="text-stone/70">(dati e richieste restano salvati anche da spento)</span>
          </span>
        </button>
        <button
          onClick={saveConfig}
          disabled={savingCfg}
          data-testid="mc-cfg-save"
          className="mt-6 inline-flex items-center gap-2 bg-espresso text-crema px-6 py-3 rounded-full font-mono text-xs uppercase tracking-[0.15em] hover:bg-fuchsiabrand transition-colors disabled:opacity-50"
        >
          {savingCfg ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />} Salva impostazioni
        </button>
      </section>

      {/* Registrations */}
      <section className="border border-espresso/10 bg-white/40 p-6 sm:p-8">
        <h3 className="font-display text-xl text-espresso mb-6 flex items-center gap-2">
          <Users size={18} className="text-fuchsiabrand" /> Iscrizioni ({regs.length})
        </h3>
        {regs.length === 0 ? (
          <p className="text-stone text-sm" data-testid="mc-regs-empty">Nessuna iscrizione ancora.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="mc-regs-list">
            {regs.map((r) => (
              <div key={r.id} className="border border-espresso/10 p-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="font-display text-espresso">{r.first_name} {r.last_name}</p>
                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-[9px] uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border ${
                      r.status === "call_booked"
                        ? "text-fuchsiabrand border-fuchsiabrand/40"
                        : r.status === "alt_requested"
                        ? "text-amber-600 border-amber-500/40"
                        : "text-stone border-espresso/20"
                    }`}>
                      {r.status === "call_booked"
                        ? "Call fissata"
                        : r.status === "alt_requested"
                        ? "Data alt. richiesta"
                        : "Iscritta"}
                    </span>
                    <button
                      onClick={() => deleteReg(r.id, `${r.first_name} ${r.last_name}`)}
                      data-testid={`mc-reg-del-${r.id}`}
                      className="text-stone hover:text-fuchsiabrand transition-colors"
                      aria-label="Elimina iscrizione"
                      title="Elimina iscrizione"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
                <a href={`mailto:${r.email}`} className="text-sm text-stone hover:text-fuchsiabrand">{r.email}</a>
                {r.phone && (
                  <p className="text-sm text-stone mt-1 flex items-center gap-1.5">
                    <Phone size={13} className="text-fuchsiabrand" />
                    <a
                      href={`https://wa.me/${(r.phone || "").replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-fuchsiabrand"
                    >
                      {r.phone}
                    </a>
                  </p>
                )}
                {r.city && (
                  <p className="text-sm text-stone mt-1 flex items-center gap-1.5">
                    <MapPin size={13} className="text-fuchsiabrand" /> {r.city}
                  </p>
                )}
                {r.call_label && (
                  <p className="text-sm text-espresso/80 mt-2 capitalize flex items-center gap-1.5">
                    <Calendar size={13} className="text-fuchsiabrand" /> {r.call_label}
                  </p>
                )}
                {r.status === "alt_requested" && (
                  <p className="text-sm text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <span className="font-semibold">Disponibilità:</span>{" "}
                    {r.availability_note ? r.availability_note : "— (nessuna preferenza indicata)"}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Waitlist */}
      <section className="border border-espresso/10 bg-white/40 p-6 sm:p-8">
        <h3 className="font-display text-xl text-espresso mb-6 flex items-center gap-2">
          <Clock size={18} className="text-fuchsiabrand" /> Lista d'attesa ({waitlist.length})
        </h3>
        {waitlist.length === 0 ? (
          <p className="text-stone text-sm" data-testid="mc-waitlist-empty">Nessuno in lista d'attesa.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2" data-testid="mc-waitlist-list">
            {waitlist.map((w) => (
              <div key={w.id} className="border border-espresso/10 p-4">
                <p className="font-display text-espresso">{w.first_name} {w.last_name}</p>
                <a href={`mailto:${w.email}`} className="text-sm text-stone hover:text-fuchsiabrand">{w.email}</a>
                {w.phone && (
                  <p className="text-sm text-stone mt-1 flex items-center gap-1.5">
                    <Phone size={13} className="text-fuchsiabrand" />
                    <a
                      href={`https://wa.me/${(w.phone || "").replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-fuchsiabrand"
                    >
                      {w.phone}
                    </a>
                  </p>
                )}
                {w.city && (
                  <p className="text-sm text-stone mt-1 flex items-center gap-1.5">
                    <MapPin size={13} className="text-fuchsiabrand" /> {w.city}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Email previews */}
      <section className="border border-espresso/10 bg-white/40 p-6 sm:p-8">
        <h3 className="font-display text-xl text-espresso mb-2 flex items-center gap-2">
          <Eye size={18} className="text-fuchsiabrand" /> Anteprima email automatiche
        </h3>
        <p className="text-stone text-sm mb-6">
          Come appaiono le email inviate automaticamente (dati di esempio, con le tue impostazioni attuali).
        </p>
        {previews.length === 0 ? (
          <p className="text-stone text-sm" data-testid="mc-preview-empty">Anteprime non disponibili.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="mc-preview-tabs">
              {previews.map((p, i) => (
                <button
                  key={p.key}
                  onClick={() => setActivePreview(i)}
                  data-testid={`mc-preview-tab-${p.key}`}
                  className={`px-4 py-2 rounded-full text-xs border transition-all duration-300 ${
                    activePreview === i
                      ? "bg-espresso text-crema border-espresso"
                      : "bg-transparent text-stone border-espresso/20 hover:border-fuchsiabrand"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="border border-espresso/10 rounded-md overflow-hidden bg-white">
              <iframe
                title="Anteprima email"
                data-testid="mc-preview-frame"
                srcDoc={previews[activePreview]?.html || ""}
                sandbox=""
                className="w-full"
                style={{ height: 520, border: "none", background: "#ffffff" }}
              />
            </div>
          </>
        )}
      </section>
    </div>
  );
};
